import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  CRM_STAGES,
  STAGE_LABEL,
  createContact,
  watchContacts,
  type CrmContact,
  type CrmStage,
} from '../services/crm';

const STAGE_COLOR: Record<CrmStage, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  NEW: 'info',
  CONTACTED: 'default',
  QUOTED: 'warning',
  WON: 'success',
  LOST: 'error',
};

export function Crm() {
  const [rows, setRows] = useState<CrmContact[]>([]);
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState<CrmStage | 'ALL'>('ALL');
  const [adding, setAdding] = useState(false);

  useEffect(() => watchContacts(setRows), []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (stage !== 'ALL' && r.stage !== stage) return false;
      if (!q) return true;
      return [r.name, r.company, r.phone, r.email].filter(Boolean).some((v) => v!.toLowerCase().includes(q));
    });
  }, [rows, search, stage]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>CRM</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
          New contact
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small" label="Search" value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ flexGrow: 1, maxWidth: 360 }}
        />
        <TextField
          size="small" select label="Stage" value={stage} onChange={(e) => setStage(e.target.value as CrmStage | 'ALL')}
          sx={{ width: 160 }}
        >
          <MenuItem value="ALL">All stages</MenuItem>
          {CRM_STAGES.map((s) => <MenuItem key={s} value={s}>{STAGE_LABEL[s]}</MenuItem>)}
        </TextField>
      </Stack>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Stage</TableCell>
              <TableCell>Tags</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5}><Typography color="text.secondary" sx={{ py: 2 }}>No contacts yet.</Typography></TableCell></TableRow>
            ) : (
              filtered.map((c) => (
                <TableRow key={c.id} hover component={RouterLink} to={`/crm/${c.id}`} sx={{ textDecoration: 'none', cursor: 'pointer' }}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>{c.company ?? '—'}</TableCell>
                  <TableCell>{c.phone ?? '—'}</TableCell>
                  <TableCell><Chip size="small" color={STAGE_COLOR[c.stage]} label={STAGE_LABEL[c.stage]} /></TableCell>
                  <TableCell>{(c.tags ?? []).slice(0, 3).map((t) => <Chip key={t} size="small" label={t} sx={{ mr: 0.5 }} />)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <NewContactDialog open={adding} onClose={() => setAdding(false)} />
    </Box>
  );
}

function NewContactDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [source, setSource] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setName(''); setCompany(''); setPhone(''); setEmail(''); setSource(''); };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await createContact({
        name: name.trim(),
        company: company.trim() || undefined,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        source: source.trim() || undefined,
        stage: 'NEW',
        tags: [],
      });
      reset();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New contact</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
          <TextField label="Company" value={company} onChange={(e) => setCompany(e.target.value)} />
          <Stack direction="row" spacing={2}>
            <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth />
            <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth />
          </Stack>
          <TextField label="Source" placeholder="e.g. WhatsApp, referral, website" value={source} onChange={(e) => setSource(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => void save()} disabled={saving || !name.trim()}>Add</Button>
      </DialogActions>
    </Dialog>
  );
}
